import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import { AlertService } from "ng/src/app/alerts/shared/alert.service";
import { Utils } from "ng/src/app/core/utils";
import { ILocationService } from "angular";
import { MenuItem, SortEvent, SortMeta } from "primeng/api";
import { catchError, finalize, map, share, switchMap } from "rxjs/operators";
import { PrimeNGHelpersService } from "../../core/services/primeng-helpers.service";
import { Table } from "primeng/table";
import {
	AlertInstanceTableEntry, AlertInstanceTableModel
} from "ng/src/app/alerts/instances/alert-instance-table.model";
import { Observable, of, Subscription, timer } from "rxjs";

@Component({
	templateUrl: './alert-instance-table.component.html',
	styleUrls: ['./alert-instance-table.component.scss'],
	selector: 'alert-instances'
})
export class AlertInstanceTableComponent implements AfterViewInit, OnDestroy {
	
	@ViewChild('alertTable') table?: Table
	
	constructor(
		private alertService: AlertService,
		@Inject('$location') private $location: ILocationService
	) {}
	
	lastRefresh?: string
	model: AlertInstanceTableModel[] = []
	loading = false;
	autoRefreshTime?: number
	refreshOptions: (MenuItem & { items: (MenuItem & { value: number | null })[] })[] = [
		{
			label: 'Auto Refresh Options',
			items: [
				{ value: null, label: 'Disabled', command: (ev) => this.setAutoRefreshTime(null, ev), icon: 'pi pi-plus' },
				{ value: 30, label: '30 Seconds', command: (ev) => this.setAutoRefreshTime(30, ev) },
				{ value: 60, label: '1 Minute', command: (ev) => this.setAutoRefreshTime(60, ev) },
				{ value: 180, label: '3 Minutes', command: (ev) => this.setAutoRefreshTime(180, ev) },
				{ value: 380, label: '5 Minutes', command: (ev) => this.setAutoRefreshTime(300, ev) }
			]
		}
	]
	
	private _selected?: AlertInstanceTableModel;
	private _initSort: SortMeta[] = [];
	private _showInitStatuses = false;
	private _updateOb$?:  Observable<AlertInstanceTableModel[]>
	private _refreshSub?: Subscription;
	
	get selected() {
		return this._selected;
	}
	
	get isMobile() {
		return Utils.isMobile;
	}
	
	set selected(val) {
		this._selected = val;
		this.$location.search('alert', val?.alert?.complexObject?.id ?? null).replace();
		
		// Sets initial sort of the new table for the selected element.
		const base: SortMeta[] = [];
		this.selected?.alert.parameters.forEach(p =>
		{
			if (!p.key) return;
			base.push({
				field: p.id.toString(),
				order: 1
			});
		});
		
		if (!base.length) {
			base.push({ field: 'created', order: -1});
		}
		
		this._initSort = base;
	}
	get selectedEntries() {
		return this.selected?.entries ?? [];
	}
	
	get selectedParameters() {
		return this.selected?.alert?.parameters ?? [];
	}
	
	get selectedHasInitStatuses() {
		return this.selected?.hasInitializedStatuses ?? false;
	}
	
	get showInitStatuses() {
		return this._showInitStatuses;
	}
	
	set showInitStatuses(val) {
		this._showInitStatuses = val;
		this.$location.search('showAll', val ? true : null)
			.replace();
	}
	
	get defaultSort() {
		return this._initSort;
	}
	
	ngAfterViewInit(): void {
		const refreshTime = parseInt(this.$location.search()['refresh']);
		this._showInitStatuses = this.$location.search()['showAll'] === true;
		this.setAutoRefreshTime(isNaN(refreshTime) ? null : refreshTime);
		this.refresh();
	}
	
	ngOnDestroy(): void {
		this._refreshSub?.unsubscribe();
	}
	
	setAutoRefreshTime(value: number | null, event: { item: MenuItem } | undefined = undefined) {
		if (value && value < 30) value = 30;
		this.autoRefreshTime = value ?? undefined;
		this.$location.search('refresh', value);
		this.refreshOptions[0]?.items?.forEach(item => item.icon = undefined);
		if (!event || !event.item) {
			this.refreshOptions[0]?.items?.forEach((item: (MenuItem & { value: number | null })) =>
				item.icon = item.value === value ? 'pi pi-check' : undefined)
		}
		else {
			event.item.icon = 'pi pi-check';
		}
		this.createAutoRefresh();
	}
	
	refresh() {
		this.createRefresh().subscribe(this.updateModel.bind(this));
	}
	
	private createRefresh(): Observable<AlertInstanceTableModel[]> {
		this.loading = true;
		return this._updateOb$ ?? (this._updateOb$ = this.alertService.getEvents().pipe(
			map((events) => {
				return Object.values(AlertInstanceTableModel.fromEvents(events.filter(ev => !ev.alert.template)))
					.sort(Utils.createSortFunction(model => model.alert.name))
			}),
			finalize(() => {
				this.lastRefresh = new Date().toISOString();
				this.loading = false;
				delete this._updateOb$;
			}),
			share()
		));
	}
	
	sortSelected(sort: SortEvent) {
		const meta = sort.multiSortMeta ?? [];
		
		const sortFuncs = meta.map((meta) => {
			const order = meta.order > 0 ? 'asc' : 'desc';
			return Utils.createSortFunction<AlertInstanceTableEntry>(entry => {
				if (meta.field === 'created')
					return new Date(entry.event.created);
				else if (meta.field === 'status')
					return entry.event.status.name;
				
				return entry.values.find(v => v.id.toString() === meta.field)?.displayValue ?? '';
				
			}, order)
		});
		
		(sort.data ?? []).sort((a, b) => {
			let sortVal = 0;
			for (let i = 0; i < sortFuncs.length; i++) {
				sortVal = sortFuncs[i](a, b);
				
				if (sortVal !== 0)
					break;
			}
			return sortVal;
		})
	}
	
	private updateModel(events: AlertInstanceTableModel[]) {
		const selectedId = this.$location.search()['alert'];
		let selectedIdx: number = -1;

		this.model = events;
		this.selected = undefined;
		this.model.forEach((v, idx) => {
			if (selectedId == v.alert.complexObject.id)
				selectedIdx = idx;
			
			v.alert.parameters.sort(Utils.createSortFunction(p => p.sortOrder));
		});
		this.selected = this.model[selectedIdx];
		setTimeout(() => {
			if (this.table)
				PrimeNGHelpersService.disableDeselectionOfRow(this.table);
		})
	}
	
	private createAutoRefresh() {
		this._refreshSub?.unsubscribe();
		if (!this.autoRefreshTime) return;
		
		this._refreshSub = timer(0, this.autoRefreshTime * 1000)
			.pipe(
				switchMap(() => this.createRefresh()
					.pipe(catchError(() => of(this.model)))
				)
			)
			.subscribe(this.updateModel.bind(this));
	}
}